import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import moment from 'moment-timezone';
import { FormattedMessage } from 'react-intl';
import messages from '../../../text/messages';
import { getDaysOfWeek, isSameDay, isSameWeek, getWeekRange, jobHasStarted, abbrDateYear } from '../../../utilities/dateTime';
import { findTimesheets, getWorkerShifts, getWorkerBillings, getJobProjects, saveTimeEntry, submitTimesheet } from '../../../reducers/timekeeping';
import { timeObjectFromMinutes, timeObjectFromMoment, isWeekend, timeObjectToMinutes, minutesFraction } from '../../../utilities/time';
import { TIMESHEET_STATUS_LOCKED } from '../../../text/enumerations';
import WorkDay from './workDay';
import PreviewWeek from './previewWeek';
import AddTime from './addTime';
import { withScreenSize } from '../../../components/hoc';
import { history } from '../../../configuration/store';
import BillingList from './billings';

class WorkWeek extends React.Component {
    constructor(props) {
        super(props);
        const { isMobile } = props;

        this.state = {
            isMobile,
            week: undefined,
            workOnWeekend: false,
            addTime: {
                modal: false,
                day: null
            }
        };

        this.submitShift = this.submitShift.bind(this);
        this.renderProjectRows = this.renderProjectRows.bind(this);
        this.getWeekTotal = this.getWeekTotal.bind(this);
        this.initialize = this.initialize.bind(this);
        this.handleAddTime = this.handleAddTime.bind(this);
        this.closeAddTime = this.closeAddTime.bind(this);
        this.getTimeLineProps = this.getTimeLineProps.bind(this);
        this.toggleWeekendWork = this.toggleWeekendWork.bind(this);
        this.reload = this.reload.bind(this);
        this.renderReadOnly = this.renderReadOnly.bind(this);
    }

    initialize(props) {
        const { date, job, timesheet, getWorkerBillings, getJobProjects } = props;

        const displayedWeek = getDaysOfWeek(date, job.work_timezone);
        const dayIndex = displayedWeek.findIndex(d => isSameDay(d, date));
        this.setState({ ...this.state, week: displayedWeek, dayIndex }, () => {
            getWorkerBillings(timesheet ? timesheet.id : undefined);
            getJobProjects(job.id);
        });
    }

    componentDidMount() {
        this.initialize(this.props);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!isSameWeek(this.props.date, nextProps.date, this.props.job.work_timezone)) {
            this.initialize(nextProps);
        }
    }

    toggleWeekendWork() {
        this.setState({ workOnWeekend: !this.state.workOnWeekend });
    }

    getTimeLineProps(day, earlistStart, latestEnd, shifts) {
        let dayFrom = earlistStart || 7 * 60;
        let dayTo = latestEnd || 19 * 60;

        const { job, projects } = this.props;
        const isW2 = job.classify_result !== 'ic';
        const projs = projects.reduce((a, c) => {
            a[c.id] = { color: c.color || '#75a535', name: c.name };
            return a;
        }, {});

        if (!shifts || !shifts.length || !job) {
            const iv = timeObjectFromMinutes(0, false);
            const ss = moment()
                .startOf('day')
                .add(8, 'hour');
            iv['time_in$new'] = timeObjectFromMoment(ss);
            iv['time_out$new'] = timeObjectFromMoment(ss.clone().add(4, 'hours'));

            return {
                to: dayTo,
                from: dayFrom,
                shifts: [],
                iniValues: iv,
                totallMins: 0,
                totalTime: timeObjectFromMinutes(0, false),
                day
            };
        }

        shifts.sort((a, b) => moment(a.time_in).tz(job.work_timezone) - moment(b.time_in).tz(job.work_timezone));

        let iniValues = {};

        const firstShitstartDay = day.startOf('day');
        const firstShiftTimeIn = moment(shifts[0].time_in).tz(job.work_timezone);
        const firstShifFrom = firstShiftTimeIn.diff(firstShitstartDay, 'minutes');

        const lastShifFrom = moment
            .tz(shifts[shifts.length - 1].time_in, job.work_timezone)
            .diff(moment.tz(shifts[shifts.length - 1].time_in, job.work_timezone).startOf('day'), 'minutes');
        const lastShifTo = lastShifFrom + shifts[shifts.length - 1].minutes;
        let totallMins = 0;

        const newShifts = [];
        shifts.forEach((shift, i) => {
            if (i === 0) {
                newShifts.push({
                    time_in: moment
                        .tz(shift.time_in, job.work_timezone)
                        .startOf('day')
                        .add(dayFrom, 'minutes'),
                    minutes: firstShifFrom - dayFrom,
                    type: 'toStart'
                });
            }

            shift.type = 'regular';
            shift.time_in = moment.tz(shift.time_in, job.work_timezone);
            shift.time_out = shift.time_in.clone().add(shift.minutes, 'minutes');
            shift.color = (projs[shift.project_id] || {}).color;
            shift.projectName = (projs[shift.project_id] || {}).name;

            newShifts.push(shift);

            iniValues[`time_in$${newShifts.length - 1}`] = timeObjectFromMoment(shift.time_in);
            iniValues[`time_out$${newShifts.length - 1}`] = timeObjectFromMoment(shift.time_out);
            iniValues[`project_id$${newShifts.length - 1}`] = shift.project_id;

            const timeObjecct = timeObjectFromMinutes(shift.minutes, false);

            iniValues[`hours$${newShifts.length - 1}`] = timeObjecct.hours;
            iniValues[`minutes$${newShifts.length - 1}`] = timeObjecct.minutes;

            totallMins = totallMins + shift.minutes;

            if (i === shifts.length - 1) {
                newShifts.push({
                    time_in: shift.time_in.clone().add(shift.minutes, 'minutes'),
                    minutes: dayTo - lastShifTo || 1,
                    type: 'toEnd'
                });
            } else if (isW2) {
                const nextaShiftIn = moment.tz(shifts[i + 1].time_in, job.work_timezone);
                const diff = nextaShiftIn.diff(shift.time_out, 'minutes');
                const pause = {
                    minutes: diff,
                    time_in: shift.time_out,
                    type: 'pause',
                    projectName: 'Break'
                };
                newShifts.push(pause);
            }
        });

        const startShift = moment
            .tz(job.work_timezone)
            .startOf('day')
            .add(8, 'hours');

        const newRegular = newShifts.filter(s => s.type === 'regular');
        const newIn = newRegular.length ? newRegular[newRegular.length - 1].time_out.clone().add(1, 'hours') : startShift.clone();
        let newOut = newIn.clone().add(4, 'hours');
        if (!isSameDay(newIn, newOut)) {
            newOut.set({
                hour: '23',
                minute: '45',
                second: '00'
            });
        }
        iniValues['time_in$new'] = timeObjectFromMoment(newIn);
        iniValues['time_out$new'] = timeObjectFromMoment(newOut);

        return {
            to: dayTo,
            from: dayFrom,
            shifts: newShifts,
            totallMins,
            totalTime: timeObjectFromMinutes(totallMins, false),
            iniValues,
            day
        };
    }

    handleAddTime(day) {
        this.setState({ addTime: { modal: true, day } });
    }

    closeAddTime() {
        this.setState({ addTime: { ...this.state.addTime, modal: false } });
    }

    submitShift(shift) {
        const { week } = this.state;
        const { job, timesheet, saveTimeEntry, getWorkerShifts, findTimesheets } = this.props;
        saveTimeEntry(shift, () => {
            getWorkerShifts(timesheet ? timesheet.id : undefined);
            const { from, to } = getWeekRange(week[1], job.work_timezone);
            findTimesheets(job.id, from, to);
        });
    }
    reload(callback = () => null) {
        const { week } = this.state;
        const { job, timesheet, getWorkerShifts, findTimesheets } = this.props;

        getWorkerShifts(timesheet ? timesheet.id : undefined, callback);
        const { from, to } = getWeekRange(week[1], job.work_timezone);
        findTimesheets(job.id, from, to);
    }

    renderProjectRows(isPreview) {
        const { week, addTime, isMobile, workOnWeekend } = this.state;
        const { date, job, timesheet, shifts, deliverables, expenses, projectsOptions, projects } = this.props;
        const isW2 = job.classify_result !== 'ic';
        const lastShiftProject = shifts.length ? shifts[shifts.length - 1].project_id : undefined;
        const isJobStarted = jobHasStarted(job);

        let earliestStart = Math.min(...shifts.map(s => timeObjectToMinutes(timeObjectFromMoment(moment.tz(s.time_in, job.work_timezone)))));
        let latestEnd = Math.max(...shifts.map(s => timeObjectToMinutes(timeObjectFromMoment(moment.tz(s.time_in, job.work_timezone))) + s.minutes));

        const dayDiff = latestEnd - earliestStart - 12 * 60;
        if (!isW2) {
            earliestStart = 0;
            latestEnd = 12 * 60;
        } else if (dayDiff < 0) {
            earliestStart += dayDiff / 2;
            latestEnd -= dayDiff / 2;
        }

        const timeline = week.map(day =>
            this.getTimeLineProps(day, earliestStart, latestEnd, shifts.filter(s => isSameDay(day, moment.tz(s.time_in, job.work_timezone))))
        );

        const isDisabled =
            !isSameWeek(date, moment.tz(job.work_timezone), job.work_timezone) || (timesheet && TIMESHEET_STATUS_LOCKED.includes(timesheet.status));

        const hasWeekendWork = !isW2 || workOnWeekend || timeline.some(t => isWeekend(t.day) && t.totallMins);
        const timeSheetWeekTotalMinutes = timeline.reduce((a, c) => a + c.totallMins, 0);
        const timeSheetWeekTotal = timeObjectFromMinutes(timeSheetWeekTotalMinutes, false);

        return (
            <div>
                {((!isPreview && job.pay_type === 'h' && isJobStarted) || (isPreview && !!timeSheetWeekTotalMinutes)) && (
                    <React.Fragment>
                        {!isPreview && (
                            <AddTime
                                {...addTime}
                                week={week}
                                projects={projectsOptions}
                                job={job}
                                timesheet={timesheet}
                                timelines={timeline}
                                reload={this.reload}
                                close={this.closeAddTime}
                                handleAddTime={this.handleAddTime}
                                isMobile={isMobile}
                                hasWeekendWork={hasWeekendWork}
                                isW2={isW2}
                                lastShiftProject={lastShiftProject}
                            />
                        )}
                        {week.map((day, j) => {
                            const showWeekend = !isWeekend(day) || hasWeekendWork;
                            return (!isPreview || (isPreview && timeline[j].totallMins)) && showWeekend ? (
                                <WorkDay
                                    key={j}
                                    form={`day-${j}`}
                                    initialValues={timeline[j].iniValues}
                                    index={j}
                                    projects={projectsOptions}
                                    job={job}
                                    date={day.tz(job.work_timezone)}
                                    day={day}
                                    timeline={timeline[j]}
                                    timesheet={timesheet}
                                    save={this.submitShift}
                                    disbled={isDisabled}
                                    isPreview={isPreview}
                                    handleAddTime={this.handleAddTime}
                                    isMobile={isMobile}
                                />
                            ) : null;
                        })}
                        {!hasWeekendWork &&
                            !isPreview && (
                                <div className="text-primary pointer" onClick={this.toggleWeekendWork}>
                                    <FormattedMessage {...messages.weekendWork} />
                                </div>
                            )}

                        <div className="row">
                            <div className="col-xs-9 col-sm-8 ">
                                <span className="preview-time">
                                    <FormattedMessage {...messages.totalHours} />
                                </span>
                            </div>
                            <div className={`col-xs-3 col-sm-2`}>
                                <span className="preview-time">
                                    {parseInt(timeSheetWeekTotal.hours, 10)} {minutesFraction(timeSheetWeekTotal.minutes)} h
                                </span>
                            </div>
                        </div>
                    </React.Fragment>
                )}
                <BillingList
                    projectsOptions={projectsOptions}
                    projects={projects}
                    job={job}
                    timesheetId={timesheet.id}
                    billings={deliverables}
                    isPreview={isPreview}
                    billingType="deliverable"
                    noAdd={isW2}
                />
                <BillingList
                    projectsOptions={projectsOptions}
                    projects={projects}
                    job={job}
                    timesheetId={timesheet.id}
                    billings={expenses}
                    isPreview={isPreview}
                    billingType="expense"
                    noAdd={!isW2}
                />
            </div>
        );
    }

    getWeekTotal() {
        const { shifts } = this.props;

        const mins = (shifts || []).reduce((a, d) => a + (d ? d.minutes : 0), 0);
        return timeObjectFromMinutes(mins, false);
    }

    navigateTimeSheets() {
        history.push('/time', { defaultTab: 1 });
    }

    renderReadOnly(fromTo, weekTotal) {
        const { isMobile } = this.props;

        return (
            <div>
                <div className="row">
                    <h4 className="week-fromto">
                        <FormattedMessage {...messages.timesheetWeek} values={fromTo} />
                    </h4>
                </div>
                <div className="row">
                    <div className={isMobile ? '' : 'col-xs-12'}>{this.renderProjectRows(true)}</div>
                </div>
            </div>
        );
    }

    renderTimesheetConfirm(fromTo, weekTotal, timesheet, isMobile, isConfirmed) {
        const { job } = this.props;
        const isInvoice = job.classify_result === 'ic' || job.pay_type !== 'h';

        const className = isMobile ? 'col-xs-12' : 'col-sm-5';
        return (
            <div className={className}>
                <PreviewWeek
                    shifts={this.renderProjectRows(true)}
                    fromTo={fromTo}
                    timesheet={timesheet}
                    form={`preview-${timesheet.id}`}
                    weekTotal={weekTotal}
                    iniValues={{ worker_notes: timesheet.worker_notes }}
                    reload={this.reload}
                    isMobile={isMobile}
                    isIC={isInvoice}
                    disabled={isConfirmed}
                />
            </div>
        );
    }

    renderReadWrite(fromTo, weekTotal, timesheet, dashboard, isMobile, isConfirmed) {
        const { job } = this.props;
        const isIC = job.classify_result === 'ic';

        const seeTimesheetsLabel = isIC ? messages.seeInvoices : messages.seeTimesheets;

        return (
            <div className="row">
                <div className={`col-xs-12 col-sm-${dashboard ? 12 : 8}`}>
                    <div className="row timesheet-title">
                        <div className="col-xs-12 col-sm-7 week">
                            <div className="title-wrapper">
                                <h4>
                                    <FormattedMessage {...messages.timesheetWeek} values={fromTo} />
                                </h4>
                                {dashboard && (
                                    <span className="title-link" onClick={this.navigateTimeSheets}>
                                        <FormattedMessage {...seeTimesheetsLabel} />
                                    </span>
                                )}
                            </div>
                        </div>
                        {!isMobile && !isConfirmed && this.renderTimesheetConfirm(fromTo, weekTotal, timesheet, isMobile, isConfirmed)}
                    </div>
                    <div className="row">
                        <div className="col-xs-12 time-wrapper">{this.renderProjectRows()}</div>
                    </div>
                    <div className="mobile-confirmation">
                        {isMobile && !isConfirmed && this.renderTimesheetConfirm(fromTo, weekTotal, timesheet, isMobile, isConfirmed)}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { week } = this.state;
        const { job, timesheet, projects, isMobile, readOnly, dashboard, ts, shifts, expenses, deliverables } = this.props;

        const billings = shifts.concat(expenses).concat(deliverables);
        const lastBillingUpdatedAt = billings.reduce((lastDate, billing) => {
            const billingUpdatedAt = moment(billing.updated_at);
            return billingUpdatedAt.isAfter(lastDate) ? billingUpdatedAt : lastDate;
        }, moment.tz(new Date(0), ts.job.job_timezone));

        if (!week || !projects || !timesheet) return null;

        const fromTo = {
            from: abbrDateYear(moment.tz(week[0], job.work_timezone)),
            to: abbrDateYear(moment.tz(week[6], job.work_timezone))
        };

        const weekTotal = this.getWeekTotal();

        const isConfirmed =
            ts.worker_confirmed_at && (!lastBillingUpdatedAt.toDate().getTime() || moment(ts.worker_confirmed_at).isAfter(lastBillingUpdatedAt));

        return readOnly ? this.renderReadOnly(fromTo, weekTotal) : this.renderReadWrite(fromTo, weekTotal, timesheet, dashboard, isMobile, isConfirmed);
    }
}

WorkWeek.defaultProps = {
    date: undefined,
    job: undefined,
    timesheet: undefined,
    shifts: [],
    deliverables: [],
    readOnly: false,
    dashboard: false
};
WorkWeek.propTypes = {
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    job: PropTypes.object.isRequired,
    timesheet: PropTypes.object.isRequired,
    shifts: PropTypes.array,
    expenses: PropTypes.array,
    deliverables: PropTypes.array,
    readOnly: PropTypes.bool,
    dashboard: PropTypes.bool
};

const mapStateToProps = (state, props) => {
    const { timesheet } = props;
    const projectsOptions = (state.timekeeping.projects || []).map(p => ({ value: p.id, label: p.name }));

    return {
        role: state.identity.role,
        projects: state.timekeeping.projects,
        projectsOptions,
        ts: timesheet || state.timekeeping.rangeTimesheets.find(t => t.id === timesheet.id),
        timesheet,
        shifts: state.timekeeping.workerShifts,
        deliverables: state.timekeeping.workerDeliverables || [],
        expenses: state.timekeeping.workerExpenses || []
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            findTimesheets,
            getWorkerShifts,
            getWorkerBillings,
            getJobProjects,
            saveTimeEntry,
            submitTimesheet
        },
        dispatch
    );

WorkWeek = connect(mapStateToProps, mapDispatchToProps)(WorkWeek);

export default withScreenSize(WorkWeek);



// WEBPACK FOOTER //
// ./src/pages/timekeeping/components/workWeek.jsx